import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { SessionMenu } from "../SessionMenu/SessionMenu";
import { join } from "../../utils/classNames";
import {
	PanelIcon,
	FullscreenEnterIcon,
	FullscreenExitIcon,
	CloseIcon,
	ChevronDown,
	FilesIcon,
	Sun,
	Moon,
} from "../icons";
import styles from "./Header.module.css";

interface Props {
	/** Conversation title shown on the left. */
	title: string;
	/** When true, a scroll shadow is drawn under the header. */
	scrolled?: boolean;
	/** Renders a sidebar toggle (top-left) when provided. */
	onSidebarToggle?: () => void;
	/** Drives the sidebar toggle's label/state. */
	sidebarOpen?: boolean;
	/** Renders a close button (top-right) when provided. */
	onClose?: () => void;
	/** Renders a fullscreen toggle when provided. */
	onFullScreenClick?: () => void;
	/** Drives the fullscreen icon (expand vs. collapse). */
	isFullScreen?: boolean;

	/** Current conversation favourite state (drives the title menu). */
	favourite?: boolean;
	/** When provided (with a non-empty title), the title becomes a menu with Favourite/Rename/Delete. */
	onRename?: (name: string) => void;
	onToggleFavourite?: () => void;
	onDelete?: () => void;
	/** Renders a Files icon button when provided. */
	onFiles?: () => void;
	/** Renders a Share button when provided. */
	onShare?: () => void;
	/** Renders a compact light/dark toggle (right of Share) when provided. */
	onThemeToggle?: () => void;
	/** Current theme, drives the toggle icon (sun on dark, moon on light). */
	theme?: "light" | "dark";
}

export const Header: React.FC<Props> = ({
	title,
	scrolled,
	onSidebarToggle,
	sidebarOpen,
	onClose,
	onFullScreenClick,
	isFullScreen,
	favourite,
	onRename,
	onToggleFavourite,
	onDelete,
	onFiles,
	onShare,
	onThemeToggle,
	theme,
}) => {
	const [renaming, setRenaming] = useState(false);
	const [draft, setDraft] = useState(title);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (renaming && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [renaming]);

	const startRename = () => {
		setDraft(title);
		setRenaming(true);
	};

	const commitRename = () => {
		if (!renaming) return;
		setRenaming(false);
		const trimmed = draft.trim();
		if (trimmed && trimmed !== title) onRename?.(trimmed);
	};

	// The title becomes a menu only when there's a real conversation and the handlers exist.
	const hasMenu = !!title && !!onRename && !!onToggleFavourite && !!onDelete;

	return (
		<div className={join(styles.base, scrolled && styles.scrolled)}>
			<div className={styles.left}>
				{onSidebarToggle && (
					<button
						type="button"
						className={styles.iconBtn}
						onClick={onSidebarToggle}
						aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
						aria-pressed={sidebarOpen}
					>
						<PanelIcon />
					</button>
				)}
				{renaming ? (
					<input
						ref={inputRef}
						className={styles.renameInput}
						value={draft}
						aria-label="Rename conversation"
						onChange={(e) => setDraft(e.target.value)}
						onBlur={commitRename}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								commitRename();
							} else if (e.key === "Escape") {
								e.preventDefault();
								setRenaming(false);
							}
						}}
					/>
				) : hasMenu ? (
					<SessionMenu
						favourite={favourite}
						onToggleFavourite={onToggleFavourite!}
						onRename={startRename}
						onDelete={onDelete!}
						triggerClassName={styles.titleBtn}
						triggerAriaLabel="Conversation options"
						triggerContent={
							<>
								<span className={styles.titleText} title={title}>
									{title}
								</span>
								<ChevronDown />
							</>
						}
						align="left"
					/>
				) : (
					<span className={styles.title} title={title}>
						{title}
					</span>
				)}
			</div>
			<div className={styles.actions}>
				{onFiles && (
					<button type="button" className={styles.iconBtn} onClick={onFiles} aria-label="Files">
						<FilesIcon />
					</button>
				)}
				{onShare && (
					<button type="button" className={styles.shareBtn} onClick={onShare}>
						Share
					</button>
				)}
				{onThemeToggle && (
					<button
						type="button"
						className={styles.iconBtn}
						onClick={onThemeToggle}
						aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
					>
						{theme === "light" ? <Moon /> : <Sun />}
					</button>
				)}
				{onFullScreenClick && (
					<button
						type="button"
						className={styles.iconBtn}
						onClick={onFullScreenClick}
						aria-label={isFullScreen ? "Exit fullscreen" : "Enter fullscreen"}
					>
						{isFullScreen ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
					</button>
				)}
				{onClose && (
					<button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
						<CloseIcon />
					</button>
				)}
			</div>
		</div>
	);
};
